import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { IProductItem, IStoreAvailability } from '@inter-ikea-kompis/types';

import ItemUtility from '../../utilities/ItemUtility.js';
import ServiceFetch from '../../utilities/ServiceFetch.js';
import type IDexfStoreAvailabilityResponse from './types/IDexfStoreAvailabilityResponse.js';

/**
 * This class is used for fetching store availability data.
 */
export default class DexfStoreAvailabilityService {
	protected maxItemsPerRequest = 50;
	private maxNumProducts = 150;
	private endpoint: string;
	private dexfApiKey: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.endpoint Endpoint.
	 * @param options.dexfApiKey DEXF Api key.
	 */
	constructor(options: { endpoint: string; dexfApiKey: string }) {
		this.endpoint = options.endpoint;
		this.dexfApiKey = options.dexfApiKey;
	}

	/**
	 * Authenticates and returns user.
	 *
	 * @param storeId Store ID.
	 * @param items Item.
	 * @returns Promise.
	 */
	public async getStoreAvailabilities(
		storeId: string,
		items: IProductItem[]
	): Promise<IStoreAvailability[]> {
		if (items.length > this.maxNumProducts) {
			throw new ServiceException(
				`Max number of articles exceeded, ${this.maxNumProducts}. Too many products to be handled by this service. Try using the webplanner service instead.`
			);
		}

		const sets = ItemUtility.getItemSets(items, this.maxItemsPerRequest);
		const options = {
			credentials: <RequestCredentials>'same-origin',
			headers: {
				'dexf-api-key': this.dexfApiKey
			}
		};

		const promises = sets.map((set) => {
			const itemId = ItemUtility.getItemsString(set);
			const url = `${this.endpoint.replace(
				'{storeId}',
				encodeURIComponent(storeId.replace(/ /g, ''))
			)}?filter.itemId=${itemId}`;
			return ServiceFetch.json(url, options);
		});

		const responses = <IDexfStoreAvailabilityResponse[]>await Promise.all(promises);

		return this.transformResponse(responses);
	}

	/**
	 * Transforms the response and returns only valid items.
	 *
	 * @param responses Responses.
	 * @returns Valid items.
	 */
	private transformResponse(responses: IDexfStoreAvailabilityResponse[]): IStoreAvailability[] {
		const result: IStoreAvailability[] = [];
		for (const response of responses) {
			for (const item of response.data) {
				if (item.valid && item.content) {
					const storeAvailability: IStoreAvailability = {
						itemId: item.itemId,
						content: {
							itemType: item.content.itemType,
							itemNo: item.content.itemNo,
							availableForCashCarry: item.content.availableForCashCarry
						}
					};

					if (item.content.cashCarry) {
						storeAvailability.content.cashCarry = {
							messageType: item.content.cashCarry.messageType,
							unitType: item.content.cashCarry.unitType,
							quantity: item.content.cashCarry.quantity,
							restock: item.content.cashCarry.restock,
							salesLocations: item.content.cashCarry.salesLocations
						};

						if ('inRange' in item.content.cashCarry) {
							storeAvailability.content.cashCarry.inRange = item.content.cashCarry.inRange;
						}
					}

					if (item.content.homeDelivery) {
						storeAvailability.content.homeDelivery = item.content.homeDelivery;
					}

					result.push(storeAvailability);
				}
			}
		}

		return result;
	}
}
